import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function ReminderNotifications() {
  const [reminders, setReminders] = useState([]);
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    requestNotificationPermission();
    fetchReminders();
    const interval = setInterval(fetchReminders, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === "granted");
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get("/reminders");
      const now = new Date();
      const dueReminders = response.data.filter((reminder) => {
        const reminderDate = new Date(reminder.reminderDate);
        return reminderDate <= now;
      });

      // Show browser notifications
      dueReminders.forEach((reminder) => {
        if (hasPermission && Notification.permission === "granted") {
          new Notification("Aufgabenerinnerung", {
            body: `${reminder.cardTitle} ist fällig!`,
            icon: "/favicon.ico",
          });
        }
      });

      setReminders(dueReminders);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    }
  };

  const dismissReminder = async (reminderId) => {
    try {
      await axios.put(`/reminders/${reminderId}/dismiss`);
      setReminders(reminders.filter((r) => r.id !== reminderId));
      toast.success("Erinnerung verworfen");
    } catch (error) {
      toast.error("Fehler beim Verwerfen der Erinnerung");
    }
  };

  if (reminders.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm" data-testid="reminder-notifications">
      {reminders.map((reminder) => (
        <Card
          key={reminder.id}
          data-testid={`reminder-${reminder.id}`}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-lg animate-in slide-in-from-right"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <Bell className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Erinnerung!</h4>
              <p className="text-sm">{reminder.cardTitle}</p>
            </div>
            <Button
              data-testid={`dismiss-reminder-${reminder.id}`}
              onClick={() => dismissReminder(reminder.id)}
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-white/20 text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
